import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getOpportunityListData,
  getOpportunityDataById,
  getOpportunityOverviewData,
} from '../opportunities/mockData'
import { getOpportunityMSTeamMemberListDataByOMSId } from '../opportunityMSTeamMembers/mockData'
import { getOpportunityMSContactListDataByOMSId } from '../opportunityMSContacts/mockData'
import { getOpportunityMSDocumentListDataByOMSId } from '../opportunityMSDocuments/mockData'
import { getOpportunityMSNoteListDataByOMSId } from '../opportunityMSNotes/mockData'
import {
  getOpportunityMSServiceListDataByOMSId,
  _getOMSSListDataByOMSId,
} from '../opportunityMSServices/mockData'
import { getLocationListDataByCompanyId } from '../locations/mockData'
import { getCompanyDataById } from '../companies/mockData'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { OpportunityMSServicesStatusIds } from '~/services/selectLists'
import { getOpportunityMasterServiceStatusByLabel } from '@/services/selectLists/opportunityMasterServicesStatii'
import { propagateOpportunityMSDataToOpportunity } from '~/cynqueModules/dataObjects/OpportunityMasterService'
import {
  IEntitySelectItemWithOptionalAvatar,
  assertIsString,
  assertIsNotUndefined,
} from '~/types/models'
import type {
  IOpportunity,
  IOpportunityMSService,
  IOpportunityMasterService,
} from '@/types/opportunity/models'
import type { ICompany } from '~/types/company/models'

const mn = '[api/msw/api/opportunityMasterServices/mockData.ts]'

const opportunityMasterServiceListData =
  undefined as unknown as IOpportunityMasterService[]

export const getOpportunityMasterServiceListData = getListDataFunctionFactory<
  IOpportunityMasterService[]
>({
  listData: opportunityMasterServiceListData,
  storageKey: 'opportunitymasterservices',
  getStartingListData: getStartingOpportunityMasterServiceListData,
  forOfFunction: async (oms) => {
    oms.opportunityMSServices = await getOpportunityMSServiceListDataByOMSId(
      oms.id
    )
    oms.opportunityMSTeamMembers =
      await getOpportunityMSTeamMemberListDataByOMSId(oms.id)
    oms.opportunityMSContacts = await getOpportunityMSContactListDataByOMSId(
      oms.id
    )
    oms.opportunityMSDocuments = await getOpportunityMSDocumentListDataByOMSId(
      oms.id
    )
    oms.opportunityMSNotes = await getOpportunityMSNoteListDataByOMSId(oms.id)
  },
})

export async function getOpportunityMasterServiceDataById(id: string) {
  const omsList = await getOpportunityMasterServiceListData()
  if (!omsList) {
    throw new Error(
      `getOpportunityMasterServiceListData returned undefined/null`
    )
  }
  const opportunityMasterService = omsList.find(
    (oms: IOpportunityMasterService) => oms.id === id
  )
  return opportunityMasterService
}

export async function getOpportunityMasterServiceListDataByOpportunityId(
  id: string
): Promise<IOpportunityMasterService[]> {
  const list = await getOpportunityMasterServiceListData()
  if (!list) {
    throw new Error(
      `getOpportunityMasterServiceListData returned undefined/null`
    )
  }
  const omsList = list.filter((oms) => oms.opportunity.id === id)
  return omsList
}

export async function getPotentialLocationSelectItemListDataByOMSId(
  id: string
) {
  const opportunityMasterService = await getOpportunityMasterServiceDataById(id)
  if (!opportunityMasterService) {
    throw new Error(
      `getOpportunityMasterServiceDataById returned undefined/null`
    )
  }
  const opportunityId = opportunityMasterService.opportunity.id
  if (typeof opportunityId !== 'string') {
    throw new TypeError(`opportunityId is not type 'string'`)
  }
  const opportunity = await getOpportunityDataById(opportunityId)
  if (!opportunity) {
    throw new Error(`getOpportunityDataById returned undefined/null`)
  }
  if (typeof opportunity.company.id !== 'string') {
    throw new TypeError(`opportunity.company.id is not type 'string'`)
  }
  const locationListData = await getLocationListDataByCompanyId(
    opportunity.company.id
  )
  return locationListData.map((loc) => {
    return { id: loc.id, label: loc.nickname }
  })
}

export async function getCompanyDataByOpportunityMasterServiceId(
  id: string
): Promise<ICompany> {
  const oms = await getOpportunityMasterServiceDataById(id)
  if (!oms) {
    throw new Error(
      `getOpportunityMasterSServiceDataById returned undefined/null`
    )
  }
  assertIsString(oms.opportunity.id)
  const opportunity = await getOpportunityDataById(oms.opportunity.id)
  if (!oms) {
    throw new Error(
      `getOpportunityMasterSServiceDataById returned undefined/null`
    )
  }
  assertIsString(opportunity.company.id)
  const company = await getCompanyDataById(opportunity.company.id)
  if (!company) {
    throw new Error(`getCompanyDataById returned undefined/null`)
  }
  return company
}

export async function getSupplierListDataByOpportunityMasterServiceId(
  id: string,
  onlyWithReadyForContractMSServices: boolean = false
): Promise<IEntitySelectItemWithOptionalAvatar[]> {
  const fn = `${mn} getSupplierDataByOpportunityMasterServiceId |`
  const oms = await getOpportunityMasterServiceDataById(id)
  if (!oms) {
    throw new Error(
      `getOpportunityMasterSServiceDataById returned undefined/null`
    )
  }
  if (!oms.opportunityMSServices) {
    console.debug(`${fn} oms has no omss. Can't get omss supplier listData'`)
    return []
  }
  return oms.opportunityMSServices.reduce(
    (
      unique: IEntitySelectItemWithOptionalAvatar[],
      omss: IOpportunityMSService
    ) => {
      if (omss.supplier && omss.doInclude) {
        if (
          !onlyWithReadyForContractMSServices ||
          omss.status.id === OpportunityMSServicesStatusIds.READY_FOR_CONTRACT
        ) {
          if (!unique.some((obj) => obj.id === omss.supplier?.id)) {
            unique.push(omss.supplier)
          }
        }
      }
      return unique
    },
    []
  )
}

export async function propagateOpportunityMasterServiceAdditions({
  newEntityData,
}: {
  newEntityData: IOpportunityMasterService
}) {
  const fn = `${mn} propagateOpportunityMasterServiceAdditions |`
  if (!newEntityData) {
    console.debug(`${fn} newEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateOpportunityMasterServiceAdditionsChangesOrDeletions({
    opportunityMasterService: newEntityData,
  })
}

export async function propagateOpportunityMasterServiceChanges({
  targetItem,
}: {
  targetItem: IOpportunityMasterService
}) {
  const fn = `${mn} propagateOpportunityMasterServiceChanges |`
  if (!targetItem) {
    console.debug(`${fn} updatedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateOpportunityMasterServiceAdditionsChangesOrDeletions({
    opportunityMasterService: targetItem,
  })
}

export async function propagateOpportunityMasterServiceDeletions({
  deletedEntityData,
}: {
  deletedEntityData: IOpportunityMasterService
}) {
  const fn = `${mn} propagateOpportunityMasterServiceDeletions |`
  if (!deletedEntityData) {
    console.debug(`${fn} deletedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateOpportunityMasterServiceAdditionsChangesOrDeletions({
    opportunityMasterService: deletedEntityData,
  })
}

async function _propagateOpportunityMasterServiceAdditionsChangesOrDeletions({
  opportunityMasterService,
}: {
  opportunityMasterService: IOpportunityMasterService
}) {
  const fn = `${mn} _propagateOpportunityMasterServiceAdditionsChangesOrDeletions |`
  console.debug(`${fn} running`)
  if (!opportunityMasterService) {
    console.debug(
      `${fn} opportunityMasterService is falsey. Nothing to propagate.`
    )
    return
  }
  const opportunity = await getOpportunityDataById(
    opportunityMasterService.opportunity.id
  )
  if (!opportunity) {
    throw new Error(`getOpportunityDataById returned undefined/null`)
  }
  const overviewData = await getOpportunityOverviewData(opportunity.id)
  assertIsNotUndefined(overviewData)
  console.debug(`${fn} overviewData:`, overviewData)

  propagateOpportunityMSDataToOpportunity({
    callingFn: fn,
    opportunity: overviewData.opportunity,
    masterServices: overviewData.opportunityMasterServices,
    setUpOpportunity: function _setUpOpportunity(opportunity: IOpportunity) {},
  })
  console.debug(
    `${fn} opportunity after propagation:`,
    overviewData.opportunity
  )

  const opportunityList = await getOpportunityListData()
  if (!opportunityList) {
    throw new Error(`getOpportunityListData returned undefined/null`)
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'opportunities',
    listData: opportunityList,
    callingFn: fn,
  })
}

export async function getStartingOpportunityMasterServiceListData(): Promise<
  IOpportunityMasterService[]
> {
  return [
    {
      id: 'XXOMS0005',
      hrid: 'OMS0005',
      descr: 'DIA - Boston, Lake & Waltham',
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      origin: {
        id: 1,
        label: 'New',
      },
      project: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Migration',
      },
      estimatedMrcAmount: 110000,
      estimatedOneTimeAmount: 6000,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 24,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [], // Will be set by forOfFn  _getOMSSListDataByOMSId('XXOMS0005', omssList),
      opportunityMSTeamMembers: [], // Will be set by forOfFn  await getOpportunityMSTeamMemberListDataByOMSId(
      //   'XXOMS0005'
      // ),
      opportunityMSContacts: [], // Will be set by forOfFn
      opportunityMSDocuments: [], // Will be set by forOfFn
    },
    // A SECOND service group on XXO0003, carrying the Coeo SDWAN kit.
    //
    // WHY IT EXISTS: it is the only fixture data whose optional items are
    // PLAIN-KIT-owned. Every other optional msService in every silo belongs to a
    // Set (DIA Bandwidth / DIA Access / IP Address / CPE / SDWAN Device), so the
    // free-form add-on branch — the "Add or Remove Items..." row entry, and the
    // half of the options dialog that can only add or remove — had no coverage
    // at all. That is why "Swap Item..." being offered on a free-form add-on,
    // where diffOptionSelections will never record a SWAPPED_OUT, went
    // unnoticed: the fixture could not render the row that shows it.
    //
    // It now carries BOTH kinds, which is the point of keeping it flat:
    //
    //   Consulting Services  optional, plain-kit-owned  -> Add or Remove
    //   License (SDWAN)      MANDATORY                  -> no row entry
    //   SDWAN Device (Set)   optional, Set-owned        -> Swap
    //
    // The four box models used to be plain-kit-owned add-ons too, which said
    // the wrong thing — a deal takes exactly one box, and only a Set can
    // express that. They moved to XXPR0064 / XXSP0023, and Consulting Services
    // became the free-form add-on in their place so this coverage survived.
    //
    // WHY SDWAN RATHER THAN E-LAN: both are plain kits with optional items, but
    // XXSP0004 "Cogent E-LAN" holds TWO E-LAN Hub kits (XXSPI0014/XXSPI0015,
    // both -> XXSP0007), each holding an E-LAN Endpoint kit, so modelling it
    // honestly means ~17 msServices across three levels of nesting. XXSP0010
    // "Coeo SDWAN" is flat: three items, no nesting beyond the one Set.
    //
    // Deliberately a NEW group rather than extra rows inside XXOMS0005: the
    // seeded lastSelectedState pins selectedMSId to XXOMS0005, so the seven e2e
    // specs that drive XXO0003's Selected Service Group card still land on the
    // unchanged DIA group.
    {
      id: 'XXOMS0010',
      hrid: 'OMS0010',
      descr: 'SDWAN - Boston',
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      origin: {
        id: 1,
        label: 'New',
      },
      project: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Migration',
      },
      estimatedMrcAmount: 0,
      estimatedOneTimeAmount: 0,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 24,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [], // Will be set by forOfFn
      opportunityMSTeamMembers: [], // Will be set by forOfFn
      opportunityMSContacts: [], // Will be set by forOfFn
      opportunityMSDocuments: [], // Will be set by forOfFn
    },
    /**
     * XXOMS0011 -- RingCentral's own service group on XXO0003, holding the one
     * RingCentral service in the fixture (XXOMSS0130, 'TAM - Bronze').
     *
     * That row used to sit in XXOMS0005, the DIA group.
     *
     * ⚠ NOT because a group may hold only one supplier -- it may, and XXOMS0005
     * still holds BOTH Cogent and Comcast. That is the whole point of a service
     * group: it collects the COMPETING quotes for one need, matching products
     * from rival suppliers side by side, with `doInclude` marking the one the
     * deal counts. Two suppliers in a group is the intended shape, not a defect.
     *
     * The problem was that 'TAM - Bronze' competes with NOTHING in there. It is
     * a Technical Account Manager, not a DIA circuit; no row in XXOMS0005 is an
     * alternative to it and it is an alternative to none of them, so `doInclude`
     * has nothing to arbitrate and the group's totals mixed two unrelated needs.
     * RingCentral quotes nothing else in this fixture, so its group has one row.
     *
     * (The supplier rule in documentation/rules/contract governs CONTRACTS, not
     * groups; contractSupplierParity.test.ts was already satisfied once XXCON0008
     * existed. This move is about what a group MEANS, not about that rule.)
     *
     * Named for its product and location like XXOMS0010 ('SDWAN - Boston'), and
     * matching its contract XXCON0008 ('TAM - Boston').
     *
     * ⚠ Moving the row moved it OFF the default card. `_setStartingIdsFromPassed-
     * Data` takes `selectedMSId` from lastSelectedState, and XXO0003's seeded row
     * pins XXOMS0005, so 'TAM - Bronze' is no longer visible until this group is
     * selected. e2e/reopenOnContractedMSService.spec.ts now clicks it first.
     */
    {
      id: 'XXOMS0011',
      hrid: 'OMS0011',
      descr: 'TAM - Boston',
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      origin: {
        id: 1,
        label: 'New',
      },
      project: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Migration',
      },
      estimatedMrcAmount: 0,
      estimatedOneTimeAmount: 0,
      term: 36,
      numLocations: 0,
      numServices: 0,
      // Its only service is CLOSED WON. Recomputed by
      // `_propagateFromOMSServicesToOMS` on load; seeded honestly so a
      // pre-cascade read is not misleading.
      status: getOpportunityMasterServiceStatusByLabel('CLOSED WON')!,
      defaultTerm: 24,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [], // Will be set by forOfFn
      opportunityMSTeamMembers: [], // Will be set by forOfFn
      opportunityMSContacts: [], // Will be set by forOfFn
      opportunityMSDocuments: [], // Will be set by forOfFn
    },
    // XXOMS0012 -- Branch Office on XXO0003, the optional nested plain Kit
    // (XXPR0068) in each state the options dialog handles: taken (Boston),
    // removed (Lake), never taken (Waltham). See
    // _buildBranchOfficeOpportunityMSServices in opportunityMSServices/mockData.ts.
    // A group of its own for the reason XXOMS0010 is: XXO0003's seeded
    // lastSelectedState pins XXOMS0005.
    {
      id: 'XXOMS0012',
      hrid: 'OMS0012',
      descr: 'Branch Office - Boston, Lake & Waltham',
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      origin: {
        id: 1,
        label: 'New',
      },
      estimatedMrcAmount: 0,
      estimatedOneTimeAmount: 0,
      term: 24,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 24,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [], // Will be set by forOfFn
      opportunityMSTeamMembers: [], // Will be set by forOfFn
      opportunityMSContacts: [], // Will be set by forOfFn
      opportunityMSDocuments: [], // Will be set by forOfFn
    },
    {
      id: 'XXOMS0006',
      hrid: 'OMS0006',
      descr: 'E-LAN - Initial Setup',
      opportunity: { id: 'XXO0005', label: 'E-LAN - Initial Setup' },
      origin: {
        id: 1,
        label: 'New',
      },
      project: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Migration',
      },
      estimatedMrcAmount: 19000,
      estimatedOneTimeAmount: 4000,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 36,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [],
      opportunityMSTeamMembers: [],
      opportunityMSContacts: [],
      opportunityMSDocuments: [],
    },
    {
      id: 'XXOMS0007',
      hrid: 'OMS0007',
      descr: 'UCaaS - Initial Seats',
      opportunity: { id: 'XXO0002', label: 'UCaaS, Initial 1K seats' },
      origin: {
        id: 1,
        label: 'New',
      },
      estimatedMrcAmount: 10250,
      estimatedOneTimeAmount: 0,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 36,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [],
      opportunityMSTeamMembers: [],
      opportunityMSContacts: [],
      opportunityMSDocuments: [],
    },
    {
      id: 'XXOMS0008',
      hrid: 'OMS0008',
      descr: 'UCaaS - Additional 500 Seats',
      opportunity: { id: 'XXO0004', label: 'UCaaS, Additional 500 seats' },
      origin: {
        id: 1,
        label: 'New',
      },
      estimatedMrcAmount: 5100,
      estimatedOneTimeAmount: 0,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 36,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [],
      opportunityMSTeamMembers: [],
      opportunityMSContacts: [],
      opportunityMSDocuments: [],
    },
    {
      id: 'XXOMS0009',
      hrid: 'OMS0009',
      descr: 'UCaaS - Initial Setup',
      opportunity: { id: 'XXO0006', label: 'UCaaS, Initial Setup' },
      origin: {
        id: 1,
        label: 'New',
      },
      estimatedMrcAmount: 7300,
      estimatedOneTimeAmount: 1000,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Rcvd')!,
      defaultTerm: 36,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [],
      opportunityMSTeamMembers: [],
      opportunityMSContacts: [],
      opportunityMSDocuments: [],
    },
    // XXOMS0013 -- the Zscaler DLP - 3 Year Kit on XXO0007, every item
    // ANNUAL. See _buildZscalerDlpOpportunityMSServices in
    // opportunityMSServices/mockData.ts.
    {
      id: 'XXOMS0013',
      hrid: 'OMS0013',
      descr: 'Zscaler DLP - 3 Year',
      opportunity: { id: 'XXO0007', label: 'Zscaler DLP - 3 Year' },
      origin: {
        id: 1,
        label: 'New',
      },
      estimatedMrcAmount: 0,
      estimatedOneTimeAmount: 0,
      term: 36,
      numLocations: 0,
      numServices: 0,
      status: getOpportunityMasterServiceStatusByLabel('Bids Selected')!,
      defaultTerm: 36,
      version: '1',
      canBeDeleted: false,
      opportunityMSServices: [],
      opportunityMSTeamMembers: [],
      opportunityMSContacts: [],
      opportunityMSDocuments: [],
    },
  ]
}
